import { useEffect, useState } from "react";
import { Loader2, Check, X, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export default function DownloadApprovals() {
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/download-requests?status=${filter}`);
      setRows(data.requests || []); setPending(data.pending_count || 0);
    } catch { toast.error("Could not load download requests."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (id, decision) => {
    setBusyId(id);
    try {
      await api.post(`/admin/download-requests/${id}/decision`, { decision });
      toast.success(decision === "approve" ? "Approved — the client can now download." : "Denied.");
      await load();
    } catch { toast.error("Could not update the request."); }
    finally { setBusyId(null); }
  };

  return (
    <div data-testid="tab-content-downloads" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[#1e3d3a]">
          <Download className="h-4 w-4 text-[#c9a961]" />
          <h3 className="font-head text-xl">Download approvals</h3>
          {pending > 0 && <span className="rounded-full bg-[#b23a48]/12 text-[#b23a48] px-2 py-0.5 text-xs">{pending} pending</span>}
        </div>
        <div className="flex items-center gap-2">
          {["pending", "approved", "denied", "all"].map((f) => (
            <button key={f} data-testid={`dl-filter-${f}`} onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 text-xs uppercase tracking-wide border transition-colors ${filter === f ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a]" : "border-[#1e3d3a]/12 text-[#1e3d3a]/50"}`}>{f}</button>
          ))}
          <button data-testid="dl-refresh" onClick={load} className="p-1.5 text-[#1e3d3a]/45 hover:text-[#c9a961]"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-[#1e3d3a]/60 py-8"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : rows.length ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-[#1e3d3a]/45 border-b border-[#1e3d3a]/10 text-xs">
              <th className="py-2 text-left font-medium">Client</th><th className="py-2 text-left font-medium">Document</th>
              <th className="py-2 text-left font-medium">Requested</th><th className="py-2 text-left font-medium">Status</th><th className="py-2 text-right font-medium">Action</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`dl-row-${r.id}`} className="border-b border-[#1e3d3a]/6">
                  <td className="py-2 text-[#1e3d3a]/80">{r.client_name || r.client_id}</td>
                  <td className="py-2 text-[#1e3d3a]/70">{r.label}</td>
                  <td className="py-2 text-[#1e3d3a]/50 text-xs tabular-nums">{r.requested_at ? new Date(r.requested_at).toLocaleString() : "—"}</td>
                  <td className="py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${r.status === "approved" ? "bg-[#2f7d5b]/15 text-[#2f7d5b]" : r.status === "denied" ? "bg-[#b23a48]/12 text-[#b23a48]" : "bg-[#c9a961]/20 text-[#8a6d2f]"}`}>{r.status}</span></td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {r.status === "pending" ? (
                      <>
                        <button data-testid={`dl-approve-${r.id}`} disabled={busyId === r.id} onClick={() => decide(r.id, "approve")} className="inline-flex items-center gap-1 rounded-full bg-[#2f7d5b] px-3 py-1 text-xs text-white hover:bg-[#276a4d] disabled:opacity-50 mr-1"><Check className="h-3.5 w-3.5" /> Approve</button>
                        <button data-testid={`dl-deny-${r.id}`} disabled={busyId === r.id} onClick={() => decide(r.id, "deny")} className="inline-flex items-center gap-1 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-3 py-1 text-xs hover:bg-[#b23a48]/10 disabled:opacity-50"><X className="h-3.5 w-3.5" /> Deny</button>
                      </>
                    ) : r.status === "approved" ? (
                      <button data-testid={`dl-revoke-${r.id}`} disabled={busyId === r.id} onClick={() => decide(r.id, "deny")} className="text-xs text-[#1e3d3a]/45 hover:text-[#b23a48]">Revoke</button>
                    ) : <span className="text-xs text-[#1e3d3a]/30">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-[#1e3d3a]/50 py-8 text-center">No {filter === "all" ? "" : filter} download requests.</p>}
    </div>
  );
}
